import { useCallback, useMemo } from 'react';
import { UPDATES, type Update } from '#global/updates/updates.ts';
import { useBrowserStorage } from './useBrowserStorage.ts';

/** Bumped if the stored shape changes; a stale key just reads as "never seen anything". */
const STORAGE_KEY = 'dn-updates-seen-v2';

/**
 * Most entries the panel will ever show. Entries are meant to be pruned to roughly the last
 * month (see the authoring notes in `updates.ts`), so this should not normally bite — it is the
 * backstop that keeps the panel bounded during a busy month, or if pruning is forgotten.
 *
 * Deliberately not defined in `updates.ts`: the hook's tests mock that module, and a constant
 * imported from a mock would come back undefined.
 */
const MAX_VISIBLE_UPDATES = 5;

type StoredValue = { seenIds: string[] };

function readSeenIds(stored: unknown): ReadonlySet<string> {
  if (typeof stored !== 'object' || stored === null) return new Set();
  const { seenIds } = stored as Partial<StoredValue>;
  if (!Array.isArray(seenIds)) return new Set();
  return new Set(seenIds.filter((id): id is string => typeof id === 'string'));
}

type UseUpdates = {
  /** The entries the panel shows, newest first. */
  updates: readonly Update[];
  /** Ids this browser has already seen. Empty if the panel has never been opened. */
  seenIds: ReadonlySet<string>;
  /** Entries whose id has not been seen — a user who has never looked has unread everything. */
  unreadCount: number;
  hasUnread: boolean;
  /** Clears the dot by recording every currently visible id as seen. */
  markAllSeen: () => void;
};

/**
 * {@link useUpdates} pairs the bundled {@link UPDATES} list with the ids this browser has already
 * seen, so the account menu can show a dot when something shipped since the user last looked.
 *
 * `entries` defaults to that list. Stories pass their own, because the shipped list is empty and
 * nothing would otherwise render the unread path.
 *
 * Tracking ids rather than a high-water-mark date is what makes "unread" reliable. A date misses
 * two cases that this list will actually hit: a second entry added the same day as one already
 * seen, and an entry that merges after a newer one and so lands with an older date.
 *
 * "Seen" lives in `localStorage`, which makes it per-browser rather than per-account: signing in
 * somewhere new shows the dot again.
 */
export function useUpdates(entries: readonly Update[] = UPDATES): UseUpdates {
  const [stored, setStored] = useBrowserStorage<StoredValue>(STORAGE_KEY, 'local');

  // Sorted defensively, then capped here rather than at render so the unread count below is drawn
  // from the same list the panel shows.
  const updates = useMemo(
    () => [...entries].sort((a, b) => b.date.localeCompare(a.date)).slice(0, MAX_VISIBLE_UPDATES),
    [entries]
  );

  const seenIds = useMemo(() => readSeenIds(stored), [stored]);

  const unreadCount = useMemo(() => updates.filter((update) => !seenIds.has(update.id)).length, [updates, seenIds]);

  const markAllSeen = useCallback(() => {
    // Only what is visible, so the stored list stays bounded by the cap rather than growing with
    // every entry ever written. Pruned ids fall out on the next write and never come back.
    setStored({ seenIds: updates.map((update) => update.id) });
  }, [updates, setStored]);

  return {
    updates,
    seenIds,
    unreadCount,
    hasUnread: unreadCount > 0,
    markAllSeen,
  };
}
